import type { EAnalyticMode } from '~/modules/analytics/types';
import type { ICampaignType } from '~/modules/campaign/types';
import type { BarChartProps } from '@shopify/polaris-viz';
import type { IAnalyticMetric } from '~/types/metric';
import type { Campaign } from '~/types/graphql';

import {
  useChannelDataSeries,
  useVisitorDataSeries,
  useDeviceDataSeries,
  CardAnalyticEmpty,
  numberWithCommas,
} from '~/modules/analytics';
import { EReportSource } from '~/modules/report-analytic/types';
import { useNavigateToReport } from '~/modules/report-analytic';
import { CAMPAIGN_METRIC_TOOLTIP_BY_MODE } from '../constants';
import { ICampaignMetricKey } from '../types/chart-tooltip';
import { CardAnalyticWrapper } from './CardAnalyticWrapper';
import { GBarChart } from '~/presentation/charts';
import { VARIANT_CONFIG } from '../configs';
import { useMemo } from 'react';

export interface CardAnalyticBarChartProps {
  label: string;
  metricKey: ICampaignMetricKey;
  barChartProps?: Partial<BarChartProps>;
  badge?: {
    isNew?: boolean;
  };
  isLoading?: boolean;
  isEmptyMetricData?: boolean;
  metrics?: IAnalyticMetric[];
  metricsPrevious?: IAnalyticMetric[];
  analyticMode: EAnalyticMode;
  campaign?: Campaign;
}

export const CardAnalyticBarChart: React.FC<CardAnalyticBarChartProps> = ({
  label,
  badge,
  metrics,
  isLoading,
  metricKey,
  barChartProps,
  isEmptyMetricData,
  analyticMode,
  campaign,
}) => {
  const { handleNavigateToReport } = useNavigateToReport();

  const tooltip = useMemo(() => {
    return CAMPAIGN_METRIC_TOOLTIP_BY_MODE[analyticMode][metricKey];
  }, [analyticMode, metricKey]);

  const { mapDeviceData } = useDeviceDataSeries();
  const { mapChannelData } = useChannelDataSeries();
  const { mapVisitorData } = useVisitorDataSeries();

  const mapperConfig = useMemo(
    () => ({
      [ICampaignMetricKey.SESSIONS_BY_DEVICE_TYPE]: mapDeviceData,
      [ICampaignMetricKey.SESSIONS_BY_TRAFFIC_SOURCE]: mapChannelData,
      [ICampaignMetricKey.VISITOR_BY_TYPE]: mapVisitorData,
    }),
    [mapDeviceData, mapChannelData, mapVisitorData],
  );

  const data = useMemo(() => {
    const mapper = mapperConfig[metricKey as keyof typeof mapperConfig];
    if (!mapper) return [];

    return (
      metrics?.map((metric) => ({
        data: mapper(metric),
        name: VARIANT_CONFIG[metric.variantKey].label,
      })) ?? []
    );
  }, [metricKey, metrics, mapperConfig]);

  const handleTitleClick = () => {
    if (!campaign || !campaign.type) return;

    handleNavigateToReport(metricKey, {
      reportCampaign: {
        id: campaign.id,
        groupId: campaign.group?.id,
        type: campaign.type as ICampaignType,
      },
      mode: analyticMode,
      from: EReportSource.CAMPAIGN,
    });
  };

  return (
    <CardAnalyticWrapper
      title={label}
      tooltip={tooltip}
      badge={badge}
      isEmptyMetricData={isEmptyMetricData}
      onTitleClick={handleTitleClick}
    >
      {isEmptyMetricData ? (
        <CardAnalyticEmpty boxProps={{ paddingBlock: '300' }} />
      ) : (
        data && (
          <GBarChart
            id={`campaign-analytic-bar-chart-${metricKey}`}
            loading={isLoading}
            data={data}
            xAxisOptions={{ hide: true }}
            yAxisOptions={{
              labelFormatter: (value) => numberWithCommas(`${value}`),
            }}
            {...barChartProps}
          />
        )
      )}
    </CardAnalyticWrapper>
  );
};
